// src/belediye/services/dashboardApi.js
// Dashboard API
// - Mevcut belediyeApi axios instance'ını kullanır.
// - /dashboard/query/ ve /dashboard/widget-data/ endpoint'lerini destekler.
// - BM4 boundary endpoint'lerini de destekler.
// - Liste parametrelerini backend ile uyumlu şekilde `statuses=a&statuses=b` olarak serialize eder.
// - Debug: localStorage.DEBUG_DASH_V2 = "1"

import belediyeApi, {
  withFullscreenLoader,
  withoutGlobalLoader,
} from "./belediyeApi";

/* ----------------------------------------------------------------------------
  Debug yardımcıları
---------------------------------------------------------------------------- */

const isBrowser = typeof window !== "undefined";

const safeDebugFlag = () => {
  if (!isBrowser) return false;
  try {
    return (
      window.localStorage.getItem("DEBUG_DASH_V2") === "1" ||
      window.__DEBUG_DASH_V2__ === true
    );
  } catch {
    return false;
  }
};

const DEBUG = safeDebugFlag();

const log = (...args) => DEBUG && console.log("[dashboardApi]", ...args);
const warn = (...args) => DEBUG && console.warn("[dashboardApi]", ...args);
const errlog = (...args) => DEBUG && console.error("[dashboardApi]", ...args);

/* ----------------------------------------------------------------------------
  Constants
---------------------------------------------------------------------------- */

const DASHBOARD_ENDPOINT = "/dashboard/query/";
const DASHBOARD_WIDGET_DATA_ENDPOINT = "/dashboard/widget-data/";

// BM4
const BM4_BOUNDARIES_ENDPOINT = "/bm4/boundaries/";
const BM4_BOUNDARY_DEFAULT_ENDPOINT = "/bm4/boundaries/default/";
const BM4_MAHALLE_STATS_ENDPOINT = "/bm4/mahalle-stats/";

const STATUS_ALIASES = Object.freeze({
  all: "tümü",
  tumu: "tümü",
  tümü: "tümü",
  hepsi: "tümü",

  new: "yeni",
  yeni: "yeni",

  in_review: "işleme_alındı",
  review: "işleme_alındı",
  "işleme_alındı": "işleme_alındı",
  "işleme alındı": "işleme_alındı",
  "incelemede": "işleme_alındı",
  "incelemeye alındı": "işleme_alındı",

  solved: "çözüldü",
  "çözüldü": "çözüldü",

  unsolved: "çözülemedi",
  "çözülemedi": "çözülemedi",
});

const WIDGET_KEYS = Object.freeze([
  "latest_new",
  "waiting_new",
  "neighborhood_breakdown",
  "unit_breakdown",
  "open_age_distribution",
]);

const BM4_LEVELS = Object.freeze(["il", "ilce", "mahalle"]);

/* ----------------------------------------------------------------------------
  Yardımcılar
---------------------------------------------------------------------------- */

const temizleBosDegerler = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
};

const sayiYap = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const boolYap = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  if (typeof v === "boolean") return v;

  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "evet"].includes(s)) return true;
  if (["0", "false", "no", "hayır", "hayir"].includes(s)) return false;

  return undefined;
};

const tarihStringiMi = (v) => {
  if (typeof v !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
};

const tarihYap = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const s = String(v).trim();
  return tarihStringiMi(s) ? s : undefined;
};

const metinYap = (v) => {
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  return s || undefined;
};

const boundaryLevelNormalizeEt = (raw) => {
  const level = String(raw ?? "").trim().toLowerCase();
  if (!level) return undefined;
  return BM4_LEVELS.includes(level) ? level : undefined;
};

/**
 * Axios bazen array parametrelerini `statuses[]=...` formatında gönderebilir.
 * Backend tarafında `getlist("statuses")` beklendiği için,
 * liste alanlarını `statuses=a&statuses=b` formatında serialize ederiz.
 */
const sorguParametreleriniSerilestir = (params) => {
  const sp = new URLSearchParams();

  Object.entries(params || {}).forEach(([anahtar, deger]) => {
    if (deger === undefined || deger === null || deger === "") return;

    if (Array.isArray(deger)) {
      deger.forEach((tek) => {
        if (tek === undefined || tek === null || tek === "") return;
        sp.append(anahtar, String(tek));
      });
      return;
    }

    sp.append(anahtar, String(deger));
  });

  return sp.toString();
};

const tekilSirali = (arr) => {
  const seen = new Set();
  const out = [];

  for (const item of arr || []) {
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }

  return out;
};

const statusTokenNormalizeEt = (raw) => {
  const token = String(raw ?? "").trim();
  if (!token) return "";

  const lower = token.toLowerCase();
  return STATUS_ALIASES[lower] || token;
};

const statusListesiNormalizeEt = (inputStatuses, singleStatusFallback) => {
  let statuses = inputStatuses;

  if (statuses === undefined && singleStatusFallback !== undefined) {
    statuses = [singleStatusFallback];
  }

  if (typeof statuses === "string") {
    statuses = statuses
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(statuses)) {
    return undefined;
  }

  const normalized = tekilSirali(
    statuses
      .map(statusTokenNormalizeEt)
      .filter(Boolean)
  );

  if (normalized.length === 0) {
    return ["tümü"];
  }

  return normalized;
};

const widgetKeyNormalizeEt = (raw) => {
  const key = String(raw ?? "").trim();
  if (!key) return undefined;
  return WIDGET_KEYS.includes(key) ? key : undefined;
};

/* ----------------------------------------------------------------------------
  Param normalize — summary
---------------------------------------------------------------------------- */

/**
 * Dashboard filtre parametrelerini normalize eder.
 *
 * Kabul edilen giriş alanları:
 * - dateFrom / dateTo
 * - from / to
 * - unitId / unit
 * - status / statuses
 * - mapPointsLimit
 *
 * Backend'e gönderilen alanlar:
 * - date_from, date_to, unit_id, statuses, map_points_limit
 */
export const dashboardParametreleriniNormalizeEt = (params = {}) => {
  const dateFrom = params.dateFrom ?? params.date_from ?? params.from;
  const dateTo = params.dateTo ?? params.date_to ?? params.to;

  const unitIdRaw = params.unitId ?? params.unit_id ?? params.unit;

  const mapPointsLimitRaw =
    params.mapPointsLimit ?? params.map_points_limit ?? params.map_limit;

  const statuses = statusListesiNormalizeEt(
    params.statuses,
    params.status
  );

  return temizleBosDegerler({
    date_from: tarihYap(dateFrom),
    date_to: tarihYap(dateTo),
    unit_id: sayiYap(unitIdRaw),
    statuses,
    map_points_limit: sayiYap(mapPointsLimitRaw),
  });
};

/* ----------------------------------------------------------------------------
  Param normalize — widget detail
---------------------------------------------------------------------------- */

/**
 * Expand / lazy-load widget parametrelerini normalize eder.
 *
 * Kabul edilen giriş alanları:
 * - widget
 * - limit / pageSize
 * - offset
 * - expanded
 * - dateFrom / dateTo
 * - unitId / unit
 * - status / statuses
 *
 * Backend'e gönderilen alanlar:
 * - widget, limit, offset, expanded
 * - date_from, date_to, unit_id, statuses
 */
export const dashboardWidgetParametreleriniNormalizeEt = (params = {}) => {
  const base = dashboardParametreleriniNormalizeEt(params);

  const widgetRaw = params.widget ?? params.widgetKey;
  const limitRaw = params.limit ?? params.pageSize ?? params.page_size;
  const offsetRaw = params.offset;
  const expandedRaw = params.expanded;

  return temizleBosDegerler({
    ...base,
    widget: widgetKeyNormalizeEt(widgetRaw),
    limit: sayiYap(limitRaw),
    offset: sayiYap(offsetRaw),
    expanded: boolYap(expandedRaw),
  });
};

/* ----------------------------------------------------------------------------
  Param normalize — BM4 boundary list
---------------------------------------------------------------------------- */

/**
 * BM4 boundary liste parametrelerini normalize eder.
 *
 * Desteklenen giriş alanları:
 * - level
 * - parentId / parent_id
 * - neighborhoodId / neighborhood_id
 * - isDefault / is_default
 * - onlyActive / only_active
 * - withGeojson / with_geojson
 * - q / search
 *
 * Backend'e gönderilen alanlar:
 * - level, parent_id, neighborhood_id, is_default, only_active, with_geojson, q
 */
export const bm4BoundaryListParametreleriniNormalizeEt = (params = {}) => {
  const levelRaw = params.level;
  const parentIdRaw = params.parentId ?? params.parent_id;
  const neighborhoodIdRaw = params.neighborhoodId ?? params.neighborhood_id;
  const isDefaultRaw = params.isDefault ?? params.is_default;
  const onlyActiveRaw = params.onlyActive ?? params.only_active;
  const withGeojsonRaw = params.withGeojson ?? params.with_geojson;
  const qRaw = params.q ?? params.search;

  return temizleBosDegerler({
    level: boundaryLevelNormalizeEt(levelRaw),
    parent_id: sayiYap(parentIdRaw),
    neighborhood_id: sayiYap(neighborhoodIdRaw),
    is_default: boolYap(isDefaultRaw),
    only_active: boolYap(onlyActiveRaw),
    with_geojson: boolYap(withGeojsonRaw),
    q: metinYap(qRaw),
  });
};

/* ----------------------------------------------------------------------------
  Param normalize — BM4 boundary detail
---------------------------------------------------------------------------- */

/**
 * BM4 boundary detail parametrelerini normalize eder.
 *
 * Desteklenen giriş alanları:
 * - includeChildren / include_children
 *
 * Backend'e gönderilen alanlar:
 * - include_children
 */
export const bm4BoundaryDetailParametreleriniNormalizeEt = (params = {}) => {
  const includeChildrenRaw =
    params.includeChildren ?? params.include_children;

  return temizleBosDegerler({
    include_children: boolYap(includeChildrenRaw),
  });
};

/* ----------------------------------------------------------------------------
  Param normalize — BM4 mahalle stats
---------------------------------------------------------------------------- */

/**
 * BM4 mahalle stats parametrelerini normalize eder.
 *
 * Desteklenen giriş alanları:
 * - unitId / unit_id / unit
 * - dateFrom / date_from / from
 * - dateTo / date_to / to
 * - status / statuses
 * - boundaryId / boundary_id
 * - parentId / parent_id
 *
 * Backend'e gönderilen alanlar:
 * - unit_id, date_from, date_to, statuses, boundary_id, parent_id
 */
export const bm4MahalleStatsParametreleriniNormalizeEt = (params = {}) => {
  const dateFrom = params.dateFrom ?? params.date_from ?? params.from;
  const dateTo = params.dateTo ?? params.date_to ?? params.to;

  const unitIdRaw = params.unitId ?? params.unit_id ?? params.unit;
  const boundaryIdRaw = params.boundaryId ?? params.boundary_id;
  const parentIdRaw = params.parentId ?? params.parent_id;

  const statuses = statusListesiNormalizeEt(
    params.statuses,
    params.status
  );

  return temizleBosDegerler({
    unit_id: sayiYap(unitIdRaw),
    date_from: tarihYap(dateFrom),
    date_to: tarihYap(dateTo),
    statuses,
    boundary_id: sayiYap(boundaryIdRaw),
    parent_id: sayiYap(parentIdRaw),
  });
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — summary
---------------------------------------------------------------------------- */

/**
 * Dashboard ana sorgu endpoint'i
 * GET /dashboard/query/
 */
export const fetchDashboardQuery = async (
  params,
  requestConfig = {}
) => {
  try {
    log("fetchDashboardQuery incoming params:", params);

    const finalParams = dashboardParametreleriniNormalizeEt(params || {});
    const qs = sorguParametreleriniSerilestir(finalParams);

    log("fetchDashboardQuery normalized params:", finalParams);
    log("fetchDashboardQuery serialized query:", qs);

    if (finalParams?.unit_id !== undefined) {
      warn(
        "unit_id gönderiliyor (başkan tümü bekliyorsa kritik):",
        finalParams.unit_id
      );
    }

    const finalRequestConfig = withFullscreenLoader(
      {
        title: "Dashboard yükleniyor",
        subtitle: "Filtrelenmiş veriler alınıyor...",
      },
      {
        ...requestConfig,
        params: finalParams,
        paramsSerializer: {
          serialize: sorguParametreleriniSerilestir,
        },
      }
    );

    const { data } = await belediyeApi.get(
      DASHBOARD_ENDPOINT,
      finalRequestConfig
    );

    log("fetchDashboardQuery response meta:", data?.meta);
    log("fetchDashboardQuery response applied:", data?.uygulananFiltreler);

    const sentUnitEmpty =
      finalParams?.unit_id === undefined || finalParams?.unit_id === null;

    if (
      data?.meta?.baskanMi &&
      sentUnitEmpty &&
      (data?.meta?.birimModu === "tek" || data?.meta?.birimModu === "seçili")
    ) {
      warn(
        "ÇELİŞKİ: unit_id boş gönderildi ama backend birimModu tek/seçili döndü.",
        {
          sentUnitId: finalParams?.unit_id,
          meta: data?.meta,
          applied: data?.uygulananFiltreler,
        }
      );
    }

    return data;
  } catch (e) {
    errlog(
      "fetchDashboardQuery failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

/**
 * Dashboard sorgusu — sessiz yenileme
 */
export const fetchDashboardQueryQuiet = async (
  params,
  requestConfig = {}
) => {
  const finalParams = dashboardParametreleriniNormalizeEt(params || {});

  const { data } = await belediyeApi.get(DASHBOARD_ENDPOINT, {
    ...withoutGlobalLoader(requestConfig),
    params: finalParams,
    paramsSerializer: {
      serialize: sorguParametreleriniSerilestir,
    },
  });

  return data;
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — widget detail
---------------------------------------------------------------------------- */

/**
 * Widget detay endpoint'i
 * GET /dashboard/widget-data/
 */
export const fetchDashboardWidgetData = async (
  params,
  requestConfig = {}
) => {
  try {
    log("fetchDashboardWidgetData incoming params:", params);

    const finalParams = dashboardWidgetParametreleriniNormalizeEt(params || {});
    const qs = sorguParametreleriniSerilestir(finalParams);

    log("fetchDashboardWidgetData normalized params:", finalParams);
    log("fetchDashboardWidgetData serialized query:", qs);

    const finalRequestConfig = {
      ...withoutGlobalLoader(requestConfig),
      params: finalParams,
      paramsSerializer: {
        serialize: sorguParametreleriniSerilestir,
      },
    };

    const { data } = await belediyeApi.get(
      DASHBOARD_WIDGET_DATA_ENDPOINT,
      finalRequestConfig
    );

    log("fetchDashboardWidgetData response meta:", data?.meta);
    log("fetchDashboardWidgetData applied:", data?.uygulananFiltreler);

    return data;
  } catch (e) {
    errlog(
      "fetchDashboardWidgetData failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

/**
 * Widget detay sorgusu — sessiz iç yenileme
 */
export const fetchDashboardWidgetDataQuiet = async (
  params,
  requestConfig = {}
) => {
  const finalParams = dashboardWidgetParametreleriniNormalizeEt(params || {});

  const { data } = await belediyeApi.get(DASHBOARD_WIDGET_DATA_ENDPOINT, {
    ...withoutGlobalLoader(requestConfig),
    params: finalParams,
    paramsSerializer: {
      serialize: sorguParametreleriniSerilestir,
    },
  });

  return data;
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — BM4 default config
---------------------------------------------------------------------------- */

/**
 * BM4 varsayılan boundary config endpoint'i
 * GET /bm4/boundaries/default/
 */
export const fetchBm4BoundaryDefaultConfig = async (
  requestConfig = {}
) => {
  try {
    log("fetchBm4BoundaryDefaultConfig start");

    const { data } = await belediyeApi.get(BM4_BOUNDARY_DEFAULT_ENDPOINT, {
      ...withoutGlobalLoader(requestConfig),
    });

    log("fetchBm4BoundaryDefaultConfig response:", data);
    return data;
  } catch (e) {
    errlog(
      "fetchBm4BoundaryDefaultConfig failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

export const fetchBm4BoundaryDefaultConfigQuiet = async (
  requestConfig = {}
) => {
  const { data } = await belediyeApi.get(BM4_BOUNDARY_DEFAULT_ENDPOINT, {
    ...withoutGlobalLoader(requestConfig),
  });

  return data;
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — BM4 boundary list
---------------------------------------------------------------------------- */

/**
 * BM4 boundary liste endpoint'i
 * GET /bm4/boundaries/
 */
export const fetchBm4Boundaries = async (
  params,
  requestConfig = {}
) => {
  try {
    log("fetchBm4Boundaries incoming params:", params);

    const finalParams = bm4BoundaryListParametreleriniNormalizeEt(params || {});
    const qs = sorguParametreleriniSerilestir(finalParams);

    log("fetchBm4Boundaries normalized params:", finalParams);
    log("fetchBm4Boundaries serialized query:", qs);

    const { data } = await belediyeApi.get(BM4_BOUNDARIES_ENDPOINT, {
      ...withoutGlobalLoader(requestConfig),
      params: finalParams,
      paramsSerializer: {
        serialize: sorguParametreleriniSerilestir,
      },
    });

    log("fetchBm4Boundaries response count:", data?.count);
    return data;
  } catch (e) {
    errlog(
      "fetchBm4Boundaries failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

export const fetchBm4BoundariesQuiet = async (
  params,
  requestConfig = {}
) => {
  const finalParams = bm4BoundaryListParametreleriniNormalizeEt(params || {});

  const { data } = await belediyeApi.get(BM4_BOUNDARIES_ENDPOINT, {
    ...withoutGlobalLoader(requestConfig),
    params: finalParams,
    paramsSerializer: {
      serialize: sorguParametreleriniSerilestir,
    },
  });

  return data;
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — BM4 boundary detail
---------------------------------------------------------------------------- */

/**
 * BM4 boundary detail endpoint'i
 * GET /bm4/boundaries/:id/
 */
export const fetchBm4BoundaryDetail = async (
  boundaryId,
  params = {},
  requestConfig = {}
) => {
  try {
    const id = sayiYap(boundaryId);
    if (!id) {
      throw new Error("Geçerli bir boundaryId zorunludur.");
    }

    log("fetchBm4BoundaryDetail incoming:", { boundaryId: id, params });

    const finalParams = bm4BoundaryDetailParametreleriniNormalizeEt(params || {});
    const endpoint = `${BM4_BOUNDARIES_ENDPOINT}${id}/`;

    const { data } = await belediyeApi.get(endpoint, {
      ...withoutGlobalLoader(requestConfig),
      params: finalParams,
      paramsSerializer: {
        serialize: sorguParametreleriniSerilestir,
      },
    });

    log("fetchBm4BoundaryDetail response:", data);
    return data;
  } catch (e) {
    errlog(
      "fetchBm4BoundaryDetail failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

export const fetchBm4BoundaryDetailQuiet = async (
  boundaryId,
  params = {},
  requestConfig = {}
) => {
  const id = sayiYap(boundaryId);
  if (!id) {
    throw new Error("Geçerli bir boundaryId zorunludur.");
  }

  const finalParams = bm4BoundaryDetailParametreleriniNormalizeEt(params || {});
  const endpoint = `${BM4_BOUNDARIES_ENDPOINT}${id}/`;

  const { data } = await belediyeApi.get(endpoint, {
    ...withoutGlobalLoader(requestConfig),
    params: finalParams,
    paramsSerializer: {
      serialize: sorguParametreleriniSerilestir,
    },
  });

  return data;
};

/* ----------------------------------------------------------------------------
  API Fonksiyonları — BM4 mahalle stats
---------------------------------------------------------------------------- */

/**
 * BM4 mahalle stats endpoint'i
 * GET /bm4/mahalle-stats/
 */
export const fetchBm4MahalleStats = async (
  params,
  requestConfig = {}
) => {
  try {
    log("fetchBm4MahalleStats incoming params:", params);

    const finalParams = bm4MahalleStatsParametreleriniNormalizeEt(params || {});
    const qs = sorguParametreleriniSerilestir(finalParams);

    log("fetchBm4MahalleStats normalized params:", finalParams);
    log("fetchBm4MahalleStats serialized query:", qs);

    const { data } = await belediyeApi.get(BM4_MAHALLE_STATS_ENDPOINT, {
      ...withoutGlobalLoader(requestConfig),
      params: finalParams,
      paramsSerializer: {
        serialize: sorguParametreleriniSerilestir,
      },
    });

    log("fetchBm4MahalleStats response count:", data?.count);
    return data;
  } catch (e) {
    errlog(
      "fetchBm4MahalleStats failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
    throw e;
  }
};

export const fetchBm4MahalleStatsQuiet = async (
  params,
  requestConfig = {}
) => {
  const finalParams = bm4MahalleStatsParametreleriniNormalizeEt(params || {});

  const { data } = await belediyeApi.get(BM4_MAHALLE_STATS_ENDPOINT, {
    ...withoutGlobalLoader(requestConfig),
    params: finalParams,
    paramsSerializer: {
      serialize: sorguParametreleriniSerilestir,
    },
  });

  return data;
};